import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Sparkles, Loader2 } from "lucide-react";
import { Language } from "@/hooks/use-browser-language";

const LOADING_MESSAGES_PL = [
  "Inicjujemy połączenie z AI...",
  "Analizujemy Twój profil LinkedIn...",
  "Synchronizujemy dane z Twojej sieci...",
  "Skanujemy wzorce zaangażowania...",
  "Wykrywamy kluczowe sygnały...",
  "Zbieramy dane o Twojej aktywności...",
  "Kalibrujemy modele predykcyjne...",
  "Segregujemy informacje o konkurencji...",
  "Oczyszczamy szum w danych...",
  "Analizujemy siłę Twojej sieci...",
  "Układamy dane w wektory semantyczne...",
  "Tworzymy mapę Twoich połączeń...",
  "Integrujemy źródła danych LinkedIn...",
  "Aktywujemy warstwy neuronowe...",
  "Oceniamy jakość Twojego contentu...",
  "Wczytujemy parametry behawioralne...",
  "Analizujemy częstotliwość aktywności...",
  "Przetwarzamy sygnały z Twojej sieci...",
  "Szacujemy siłę digital footprint...",
  "Kodujemy dane do modelu AI...",
  "Optymalizujemy ścieżkę analizy...",
  "Sprawdzamy reach Twojego contentu...",
  "Łączymy wiązki informacyjne...",
  "Uspójniamy wektory komunikacji...",
  "Uruchamiamy silnik analityczny...",
  "Osadzamy dane w modelu...",
  "Przeliczamy engagement rate...",
  "Uaktywniamy inteligentne filtry...",
  "Finalizujemy analizę konkurencji...",
  "Przygotowujemy Twój raport...",
];

const LOADING_MESSAGES_EN = [
  "Initializing AI connection...",
  "Analyzing your LinkedIn profile...",
  "Syncing data from your network...",
  "Scanning engagement patterns...",
  "Detecting key signals...",
  "Gathering your activity data...",
  "Calibrating predictive models...",
  "Sorting competitor information...",
  "Cleaning data noise...",
  "Analyzing your network strength...",
  "Arranging data into semantic vectors...",
  "Creating your connection map...",
  "Integrating LinkedIn data sources...",
  "Activating neural layers...",
  "Evaluating your content quality...",
  "Loading behavioral parameters...",
  "Analyzing activity frequency...",
  "Processing signals from your network...",
  "Estimating digital footprint strength...",
  "Encoding data for AI model...",
  "Optimizing analysis path...",
  "Checking your content reach...",
  "Connecting information streams...",
  "Aligning communication vectors...",
  "Starting analytical engine...",
  "Embedding data in model...",
  "Calculating engagement rate...",
  "Activating smart filters...",
  "Finalizing competitor analysis...",
  "Preparing your report...",
];

interface QuizFinalLoaderProps {
  language?: Language;
}

export function QuizFinalLoader({ language = "pl" }: QuizFinalLoaderProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  
  const messages = language === "en" ? LOADING_MESSAGES_EN : LOADING_MESSAGES_PL;
  const title = language === "en" ? "Analyzing your answers" : "Analizujemy Twoje odpowiedzi";

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      
      setTimeout(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
        setFadeIn(true);
      }, 500);
    }, 1400);

    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <Card className="p-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
      <div className="flex flex-col items-center justify-center gap-6 py-8">
        <div className="relative">
          <div className="p-4 rounded-full bg-primary/10">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <Loader2 className="h-6 w-6 text-primary animate-spin absolute -bottom-1 -right-1" />
        </div>
        
        <div className="text-center space-y-4">
          <h3 className="text-xl font-semibold text-primary">{title}</h3>
          
          <p
            className="text-lg font-semibold text-foreground max-w-md transition-opacity duration-500"
            style={{ opacity: fadeIn ? 1 : 0 }}
          >
            {messages[currentMessageIndex]}
          </p>
        </div>
        
        <div className="flex gap-2 mt-2">
          <div className="h-2 w-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: '0ms' }} />
          <div className="h-2 w-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: '150ms' }} />
          <div className="h-2 w-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </Card>
  );
}
